/**
 * Asset Header
 *
 * Title row for the per-asset dashboard: brand icon, name, type badge,
 * a small chip strip (description / country / activity count / co2e) and
 * a slot for header actions (Edit, Delete, Add Activity, etc.).
 *
 * Below the title we render a verbose `AssetMetaSummary` that surfaces
 * every per-type field on the asset (floor area, employees, vehicle type,
 * PUE, custom intensity, etc.) so users can see the asset's metadata at
 * a glance. Sits above the dashboard's TabNav with no outer Card.
 *
 * Asset switching is intentionally NOT here — there's a "Back to Assets"
 * link directly above this component, which is the navigation users
 * already understand.
 */

import { type ReactNode } from 'react';
import {
  Car as CarIcon,
  Clock,
  FileText,
  Gauge,
  Hash,
  MapPin,
  Ruler,
  Server,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { COUNTRY_CODES } from '@g17eco/emissions-config';
import { ASSET_TYPE_BRANDING } from '../../config/assetTypeBranding';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';
import { AssetType, type EmissionAsset } from '../../types/emissions';

interface AssetHeaderProps {
  asset: EmissionAsset;
  /** Optional action(s) rendered to the right (e.g. Edit, Delete, Add Activity). */
  actions?: ReactNode;
}

interface SummaryRow {
  icon: LucideIcon;
  label: string;
  value: string;
}

const lookupCountryName = (region: string | undefined): string | undefined => {
  if (!region) return undefined;
  return COUNTRY_CODES?.find(([code]) => code === region)?.[1] ?? region;
};

export const AssetHeader = ({ asset, actions }: AssetHeaderProps) => {
  const { formatCompact } = useEmissionsFormatter();
  const brand = ASSET_TYPE_BRANDING[asset.assetType] ?? ASSET_TYPE_BRANDING[AssetType.Other];
  const countryName = lookupCountryName(asset.region);
  const co2e = formatCompact(asset.totalCo2e);

  // Inline chips next to the title — keep this short, the verbose details
  // live in the summary grid below.
  const titleChips: Array<{ icon?: LucideIcon; label: string }> = [];
  if (countryName) titleChips.push({ icon: MapPin, label: countryName });
  titleChips.push({
    label: `${asset.activityCount} activit${asset.activityCount === 1 ? 'y' : 'ies'}`,
  });
  titleChips.push({ label: co2e.display });

  return (
    <div className='tw:flex tw:items-start tw:justify-between tw:gap-3'>
      <div className='tw:flex tw:items-center tw:gap-3 tw:min-w-0 tw:w-full'>
        <div className={`tw:rounded-full tw:p-4 tw:shrink-0 ${brand.iconBg}`}>
          <brand.Icon className={`tw:size-10 ${brand.iconColor}`} />
        </div>
        <div className='tw:min-w-0 tw:w-full tw:flex tw:flex-col tw:items-start tw:gap-0'>
          <div className='tw:flex tw:items-center tw:justify-between tw:w-full'>
            <div>
              <h4 className='tw:text-xl tw:font-semibold tw:mb-0'>{asset.name}</h4>
              <div className='tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground tw:flex-wrap tw:mt-1'>
                <Badge variant='outline' className={brand.iconColor}>
                  {brand.label}
                </Badge>
                {titleChips.map((chip, i) => (
                  <span key={i} className='tw:flex tw:items-center tw:gap-1'>
                    {chip.icon && <chip.icon className='tw:size-3 tw:shrink-0' />}
                    {chip.label}
                  </span>
                ))}
              </div>
            </div>
            {actions && <div className='tw:flex tw:items-center tw:gap-2 tw:shrink-0'>{actions}</div>}
          </div>
          <AssetMetaSummary asset={asset} countryName={countryName} brandIconColor={brand.iconColor} />
        </div>
      </div>
    </div>
  );
};

interface AssetMetaSummaryProps {
  asset: EmissionAsset;
  countryName?: string;
  brandIconColor: string;
}

/**
 * Verbose read-only grid of every populated metadata field on the asset.
 * Ported from the now-removed admin AssetDetail page so the dashboard
 * surfaces the same level of detail (description, address, vehicle type,
 * identifier, group size, PUE, IT load, rack count, operating hours,
 * rated capacity, custom intensity).
 */
const AssetMetaSummary = ({ asset, countryName, brandIconColor }: AssetMetaSummaryProps) => {
  const rows: SummaryRow[] = [];

  if (asset.description) rows.push({ icon: FileText, label: 'Description', value: asset.description });
  if (countryName) rows.push({ icon: MapPin, label: 'Country', value: countryName });
  if (asset.address) rows.push({ icon: MapPin, label: 'Address', value: asset.address });

  if ('floorAreaM2' in asset && asset.floorAreaM2) {
    rows.push({ icon: Ruler, label: 'Floor Area', value: `${asset.floorAreaM2.toLocaleString()} m²` });
  }
  if ('employeeCount' in asset && asset.employeeCount) {
    rows.push({ icon: Users, label: 'Employees', value: asset.employeeCount.toLocaleString() });
  }
  if ('vehicleType' in asset && asset.vehicleType) {
    rows.push({ icon: CarIcon, label: 'Vehicle Type', value: String(asset.vehicleType) });
  }
  if ('identifier' in asset && asset.identifier) {
    rows.push({ icon: Hash, label: 'Identifier', value: String(asset.identifier) });
  }
  if (typeof asset.unitCount === 'number' && asset.unitCount > 1) {
    rows.push({ icon: Users, label: 'Group size', value: `${asset.unitCount.toLocaleString()} units` });
  }
  if ('pue' in asset && asset.pue) {
    rows.push({ icon: Gauge, label: 'PUE', value: String(asset.pue) });
  }
  if ('itLoadKw' in asset && asset.itLoadKw) {
    rows.push({ icon: Zap, label: 'IT Load', value: `${asset.itLoadKw.toLocaleString()} kW` });
  }
  if ('rackCount' in asset && asset.rackCount) {
    rows.push({ icon: Server, label: 'Rack Count', value: asset.rackCount.toLocaleString() });
  }
  if ('operatingHoursPerYear' in asset && asset.operatingHoursPerYear) {
    rows.push({ icon: Clock, label: 'Operating Hours / Year', value: `${asset.operatingHoursPerYear.toLocaleString()} hrs` });
  }
  if ('ratedCapacityKw' in asset && asset.ratedCapacityKw) {
    rows.push({ icon: Zap, label: 'Rated Capacity', value: `${asset.ratedCapacityKw.toLocaleString()} kW` });
  }
  if (asset.customIntensityLabel && typeof asset.customIntensityValue === 'number') {
    rows.push({
      icon: Gauge,
      label: asset.customIntensityLabel,
      value: asset.customIntensityValue.toLocaleString(),
    });
  }

  if (rows.length === 0) return null;

  return (
    <div className='tw:flex tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-1 tw:mt-2'>
      {rows.map((row) => (
        <div key={row.label} className='tw:flex tw:items-center tw:gap-1.5'>
          <row.icon className={cn('tw:size-3.5 tw:shrink-0', brandIconColor)} />
          <span className='tw:text-xs tw:text-muted-foreground'>{row.label}</span>
          <span className='tw:text-xs tw:font-medium'>{row.value}</span>
        </div>
      ))}
    </div>
  );
};
